import { add, formatDistanceToNow, subHours, subMinutes } from 'date-fns';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    componentAutoReaction,
    IDisposable,
    inject,
    Loader,
    PopoutHandle,
    SnapshotPopout,
    TasksStore,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowsAngleExpand } from '@react-icons/all-files/bs/BsArrowsAngleExpand';
import notFoundImage from '../../../../Mri.Web.Common/Assets/image-not-found.png';
import { AppStore } from '../../Core/AppStore';
import { ActiveDisplay } from '../ActiveDisplay';
import { DisplaysStore } from '../DisplaysStore';

export interface IDisplayGridSnapshotProps {
    readonly screenSnapshotNumber: number;
    readonly displaySerialNumber: string;
    readonly infiniteTouch?: boolean;
    readonly className?: string;
    readonly loading?: boolean;
}

@autoinject
@observer
export class DisplayGridSnapshot extends React.Component<IDisplayGridSnapshotProps> {
    
    @bind
    @action
    public async componentDidMount() {
        await this.onLoad();
    }

    public render() {
        return (
            <>
                <div
                    className={this.props.className + ' h-100 w-100 position-relative d-flex align-items-center justify-content-center'}
                    onMouseEnter={action(() => (this.showOverlay = true))}
                    onMouseLeave={action(() => (this.showOverlay = false))}
                >
                    {!this.notFound && this.screenSnapshot &&
                        <img
                            alt="Not Found"
                            onError={this.onError}
                            src={this.screenSnapshot ? this.screenSnapshot.uri : undefined}
                            className="img-fluid"
                            style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                maxHeight: '100%',
                            }}
                        />
                    }
                    
                    <div
                        className={this.snapshotOverlayClass}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            // display: this.showOverlay ? 'flex' : 'none',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10,
                        }}
                    >
                        <div className="row h-100 mx-0">
                            <div className="col-12 flex-items-center justify-content-center">
                                <div className="w-100 text-center">
                                    {this.loading && (
                                        <Loader className="text-white text-center p-2 f-16" loading={true} showSpinner/>
                                    )}

                                    <button
                                        className="btn btn-outline-white btn-xsm refresh-button"
                                        onClick={() => this.refreshSnapshot(true)}
                                    >
                                        <span>{this.loading ? "Refreshing..." : "Refresh"}</span>
                                    </button>
                                </div>
                            </div>

                            <div className="col-12 align-self-end f-12 text-center p-0">{this.updatedVerbage}</div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    @bind
    @action
    private async onLoad() {
        try {
            await this.displaysStore.loadLatestScreenSnapshotAsync(this.props.displaySerialNumber);
        } catch (e: any) {
            console.error('Error refreshing PopVu™', e);
        }

        if (this.outOfDate) {
            await this.refreshSnapshot();
        }
    }

    @bind
    @action
    private async refreshSnapshot(manual?: boolean) {
        this.refreshing = true;
        try {
            await this.displaysStore.loadCurrentScreenSnapshotAsync(this.props.displaySerialNumber, manual);
        } catch (e: any) {
            console.error('Error refreshing PopVu™', e);
        } finally {
            this.refreshing = false;
        }
    }


    @bind
    @action
    private onError(event: React.SyntheticEvent<HTMLImageElement>) {
        this.notFound = true;
    }
    
    @computed
    private get snapshotOverlayClass() {
        if (!this.screenSnapshot || this.notFound || this.loading || this.showOverlay || this.outOfDate) {
            return 'snapshot-overlay';
        }
        return 'd-none';
    }

    @computed
    private get outOfDate() {
        if (this.screenSnapshot) {
            return this.screenSnapshot.pollTime < subMinutes(new Date(), 15);
        }

        return true;
    }

    @computed
    private get loading() {
        return this.displaysStore.screenSnapshotRequests.get(this.props.displaySerialNumber);
    }

    @computed
    private get updatedVerbage() {
        const now = this.appStore.clock.time;
        const snapshot = this.screenSnapshot;
        
        if (snapshot && !this.notFound) {
            if (snapshot.pollTime < subMinutes(new Date(), 15)) return 'Out of Date';
            if (snapshot.pollTime > now) return 'Updated: A few seconds ago';
            if (snapshot.pollTime < now) {
                return (
                    formatDistanceToNow(snapshot.pollTime, {
                        addSuffix: true,
                    })
                );
            }
        }

        return this.refreshing ? 'Loading...' : 'Snap Not Found';
    }

    @computed
    private get screenSnapshot() {
        let snapshots = this.displaysStore.screenSnapshots.get(this.props.displaySerialNumber);
        snapshots = snapshots?.filter(s => this.props.infiniteTouch ? s.captureType == Mri.ScreenSnapshots.CaptureType.TouchHeatmap : s.captureType == Mri.ScreenSnapshots.CaptureType.Snapshot);
        if (snapshots && snapshots.length > 0) return snapshots[this.props.screenSnapshotNumber];
    }

    @observable
    private showOverlay = false;
    @observable
    private refreshing = false;
    @observable
    private notFound = false;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
}
